import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import DropdownField from '../../DropDownField/DropDownField';
import RequiredField from '../../../SharedComponents/RequiredField/RequiredField';
import SupervisorDropdown from '../../../Supervisor/SupervisorDropdown';
import DutyStationDropdown from '../../../DutyStation/DutyStationDropDown';
import { UNIT_OPERATIONS, MODE_OF_WORK, TYPE_OF_CONTRACT } from '../../../../Constants/constants';
import { useTranslation } from 'react-i18next';

const defaultFormData = {
    'function': '',
    section: '',
    unit: '',
    duty_station_id: '',
    office_location: '', // ADDED: office_location field
    type_of_contract: '',
    expiration_date: '',
    supervisor: '',
    mode_of_work: '',
};

const EmploymentFormSection = ({
                                   formData = {},
                                   handleChange,
                                   errors = {},
                                   touchedFields = {},
                                   requiredFields = []
                               }) => {
    const [unitOptions, setUnitOptions] = useState([]);
    const { t } = useTranslation('employment');

    // Safely merge default and provided form data
    const safeFormData = {
        ...defaultFormData,
        ...formData,
    };

    // Destructure values with null safety
    const {
        function: employmentFunction = '',
        section = '',
        unit = '',
        duty_station_id = '',
        office_location = '', // ADDED: office_location field
        type_of_contract = '',
        expiration_date = '',
        supervisor = '',
        mode_of_work = '',
    } = safeFormData;

    // Helper function to check if field should show error styling
    const shouldShowError = (fieldName) => {
        return (touchedFields[fieldName] || Object.keys(errors).length > 0) && errors[fieldName];
    };

    // Helper function to check if field is required and empty
    const isRequiredAndEmpty = (fieldName, value) => {
        return requiredFields.includes(fieldName) && (!value || value.toString().trim() === '');
    };

    // Update unit options based on section selection
    useEffect(() => {
        const sectionOptions = {
            'Operation': UNIT_OPERATIONS.Operation,
            'Program': UNIT_OPERATIONS.Program,
            'default': []
        };

        setUnitOptions(sectionOptions[section] || sectionOptions.default);
    }, [section]);

    const formFields = [
        {
            row: 1,
            fields: [
                {
                    Component: RequiredField,
                    props: {
                        label: t('function_label'),
                        name: "function",
                        value: employmentFunction || '',
                        onChange: handleChange,
                        error: shouldShowError('function') ? errors.function : null,
                        required: true,
                        isInvalid: shouldShowError('function') || isRequiredAndEmpty('function', employmentFunction),
                        className: `form-control ${shouldShowError('function') || isRequiredAndEmpty('function', employmentFunction) ? 'is-invalid border-danger' : ''}`
                    }
                },
                {
                    Component: DropdownField,
                    props: {
                        controlId: "section",
                        label: t('section_label'),
                        name: "section",
                        value: section,
                        handleChange: handleChange,
                        options: Object.keys(UNIT_OPERATIONS),
                        error: shouldShowError('section') ? errors.section : null,
                        required: true,
                        isInvalid: shouldShowError('section') || isRequiredAndEmpty('section', section),
                        className: `form-select ${shouldShowError('section') || isRequiredAndEmpty('section', section) ? 'is-invalid border-danger' : ''}`
                    }
                }
            ]
        },
        {
            row: 2,
            fields: [
                {
                    Component: DropdownField,
                    props: {
                        controlId: "unit",
                        label: t('unit_label'),
                        name: "unit",
                        value: unit,
                        handleChange: handleChange,
                        options: unitOptions,
                        error: shouldShowError('unit') ? errors.unit : null,
                        disabled: !section,
                        required: true,
                        isInvalid: shouldShowError('unit') || isRequiredAndEmpty('unit', unit),
                        className: `form-select ${shouldShowError('unit') || isRequiredAndEmpty('unit', unit) ? 'is-invalid border-danger' : ''}`
                    }
                },
                {
                    Component: DutyStationDropdown,
                    props: {
                        name: "duty_station_id",
                        value: duty_station_id,
                        onChange: handleChange,
                        label: t('duty_station_label'),
                        isInvalid: shouldShowError('duty_station_id') || isRequiredAndEmpty('duty_station_id', duty_station_id),
                        invalidFeedback: errors?.duty_station_id || t('duty_station_required') || 'Please select a duty station',
                        placeholder: t('select_duty_station_placeholder') || 'Select a duty station',
                        required: true,
                        className: `form-select ${shouldShowError('duty_station_id') || isRequiredAndEmpty('duty_station_id', duty_station_id) ? 'is-invalid border-danger' : ''}`
                    }
                }
            ]
        },
        {
            row: 3,
            fields: [
                {
                    Component: RequiredField,
                    props: {
                        label: t('office_location_label'),
                        name: "office_location",
                        value: office_location || '',
                        onChange: handleChange,
                        error: shouldShowError('office_location') ? errors.office_location : null,
                        required: false,
                        isInvalid: shouldShowError('office_location'),
                        className: `form-control ${shouldShowError('office_location') ? 'is-invalid border-danger' : ''}`,
                        placeholder: t('office_location_placeholder') || 'Enter office location'
                    }
                },
                {
                    Component: DropdownField,
                    props: {
                        controlId: "type_of_contract",
                        label: t('type_of_contract_label'),
                        name: "type_of_contract",
                        value: type_of_contract,
                        handleChange: handleChange,
                        options: TYPE_OF_CONTRACT,
                        error: shouldShowError('type_of_contract') ? errors.type_of_contract : null,
                        required: true,
                        isInvalid: shouldShowError('type_of_contract') || isRequiredAndEmpty('type_of_contract', type_of_contract),
                        className: `form-select ${shouldShowError('type_of_contract') || isRequiredAndEmpty('type_of_contract', type_of_contract) ? 'is-invalid border-danger' : ''}`
                    }
                }
            ]
        },
        {
            row: 4,
            fields: [
                {
                    type: "date",
                    props: {
                        id: "expiration_date",
                        label: t('expiration_date_label'),
                        name: "expiration_date",
                        value: expiration_date,
                        onChange: handleChange,
                        error: shouldShowError('expiration_date') ? errors.expiration_date : null,
                        required: false,
                        className: `form-control ${shouldShowError('expiration_date') ? 'is-invalid border-danger' : ''}`
                    }
                },
                {
                    Component: SupervisorDropdown,
                    props: {
                        name: "supervisor",
                        value: supervisor,
                        onChange: handleChange,
                        label: t('supervisor_label'),
                        isInvalid: shouldShowError('supervisor'),
                        invalidFeedback: errors?.supervisor,
                        placeholder: t('select_supervisor_placeholder') || 'Select a supervisor',
                        required: false,
                        className: `form-select ${shouldShowError('supervisor') ? 'is-invalid border-danger' : ''}`
                    }
                }
            ]
        },
        {
            row: 5,
            fields: [
                {
                    Component: DropdownField,
                    props: {
                        controlId: "mode_of_work",
                        label: t('mode_of_work_label'),
                        name: "mode_of_work",
                        value: mode_of_work,
                        handleChange: handleChange,
                        options: MODE_OF_WORK,
                        error: shouldShowError('mode_of_work') ? errors.mode_of_work : null,
                        required: false,
                        className: `form-select ${shouldShowError('mode_of_work') ? 'is-invalid border-danger' : ''}`
                    }
                },
                {
                    // Empty placeholder field to maintain two-column layout
                    Component: () => <div></div>,
                    props: {}
                }
            ]
        }
    ];

    const renderDateField = ({ props }) => (
        <div className="mb-3">
            <label htmlFor={props.id} className="form-label">
                {props.label}
                {props.required && <span className="text-danger ms-1">*</span>}
            </label>
            <input
                type="date"
                {...props}
                className={props.className || `form-control ${props.error ? 'is-invalid border-danger' : ''}`}
                style={{
                    borderColor: props.error ? '#dc3545' : '',
                    borderWidth: props.error ? '2px' : '1px'
                }}
            />
            {props.error && (
                <div className="invalid-feedback d-block text-danger">
                    {props.error}
                </div>
            )}
        </div>
    );

    return (
        <div>
            <style jsx>{`
                .form-control.border-danger,
                .form-select.border-danger {
                    border-color: #dc3545 !important;
                    border-width: 2px !important;
                    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
                }
                
                .form-label .text-danger {
                    font-weight: bold;
                }
                
                .is-invalid {
                    border-color: #dc3545 !important;
                }
                
                .invalid-feedback.d-block {
                    display: block !important;
                    font-size: 0.875rem;
                    margin-top: 0.25rem;
                }
            `}</style>

            {formFields.map(({ row, fields }) => (
                <Row className="mb-3" key={row}>
                    {fields.map((field, index) => (
                        <Col md={6} key={index}>
                            {field.type === 'date' ? (
                                renderDateField(field)
                            ) : (
                                <div className="mb-3">
                                    <field.Component
                                        {...field.props}
                                        style={{
                                            ...(field.props.style || {}),
                                            borderColor: field.props.isInvalid ? '#dc3545' : '',
                                            borderWidth: field.props.isInvalid ? '2px' : '1px'
                                        }}
                                    />
                                </div>
                            )}
                        </Col>
                    ))}
                </Row>
            ))}
        </div>
    );
};

export default EmploymentFormSection;